import {
  Autocomplete,
  AutocompleteProps,
  CircularProgress,
  TextField,
  TextFieldProps,
  TextFieldVariants,
  Typography,
} from "@mui/material";
import React, { useEffect } from "react";
import { UseQueryResult } from "react-query";

export type Selectable = {
  // This is really required, but it's marked as optional in KnowfullyAdminClient,
  // so we mark it as optional in this type to simplify usage
  id?: string;
};

type PaginatedResult<T> = {
  totalRecords?: number;
  data?: T[];
};

export type AutocompleteWithQueryProps<T extends Selectable> = {
  value?: string;
  onChange: (v?: string) => void;
  fetchedData: UseQueryResult<PaginatedResult<T>>;
  failMessage: string;
  className?: string;
  label?: string;
  required?: boolean;
  error?: boolean;
  textFieldProps?: Partial<TextFieldProps>;
  labelField: keyof T;
  variant?: TextFieldVariants;
  TextFieldComponent?: typeof TextField;
} & Partial<
  Omit<
    AutocompleteProps<T, false, false, false>,
    "onChange" | "value" | "multiple"
  >
>;

const AutocompleteWithQuery = <T extends Selectable>({
  fetchedData,
  failMessage,
  className,
  label,
  onChange,
  value,
  required,
  textFieldProps,
  labelField,
  variant = "standard",
  TextFieldComponent = TextField,
  ...rest
}: AutocompleteWithQueryProps<T>) => {
  const { isLoading, isError, data, error } = fetchedData;

  useEffect(() => {
    if (!isLoading && data?.data) {
      if (value && !data?.data?.find((d) => d.id === value)) {
        onChange(undefined);
      }
    }
  }, [isLoading, data]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isError) {
    console.log(error);
    return <Typography color="error">{failMessage}</Typography>;
  }

  const handleValueChange = (e: React.ChangeEvent<{}>, v: T | null) => {
    if (onChange) onChange(v?.id);
  };

  const autoCompleteValue = value
    ? data?.data?.find((d) => d.id === value) ?? null
    : null;

  return (
    <Autocomplete
      className={className}
      getOptionLabel={(option) => (option[labelField] as unknown) as string}
      options={data?.data ?? []}
      autoComplete
      value={autoCompleteValue}
      multiple={false}
      onChange={handleValueChange}
      disabled={isLoading}
      loading={isLoading}
      {...rest}
      renderInput={(params) => (
        <TextFieldComponent
          variant={variant}
          {...params}
          {...textFieldProps}
          InputProps={{
            ...textFieldProps?.InputProps,
            ...params.InputProps,
            endAdornment: (
              <>
                {isLoading ? <CircularProgress size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          InputLabelProps={{ required }}
          label={label}
          fullWidth
        />
      )}
    />
  );
};

export default AutocompleteWithQuery;
